<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch, type PropType } from 'vue'
import { BarChartSorting, SolidFill, ColorHEX, emptyLine, FontSettings, AxisTickStrategies, Themes } from '@lightningchart/lcjs'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue';
const { hasIntermediateAccess } = useAuth();

interface TotalMaterialConsumptionFromEventsItem {
  cement1: number
  cement2: number
  slurry: number
  additive1: number
  additive2: number
  additive3: number
  colloidalSilica: number
  accelerator: number
  colloidalAccelerator: number
}

interface TotalConsumedMaterialOverviewFromPumpsItem {
  holeNo: number
  cement1: number
  cement2: number
  slurry: number
  additive1: number
  additive2: number
  additive3: number
  colloidalSilica: number
  accelerator: number
  colloidalAccelerator: number
  volume: number
}

const props = defineProps({
    totalMaterialConsumedFromEvents: {
        type: Object as PropType<TotalMaterialConsumptionFromEventsItem>,
            required: true,
            default: () => ({})
        },
    totalConsumedMaterialOverviewFromPumps: {
        type: Array as PropType<TotalConsumedMaterialOverviewFromPumpsItem[]>,
        required: true,
        default: () => []
  }
});

const fmt = (val: number | undefined | null) => {
  if (val === undefined || val === null) return '0';
  return val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const extractTotalMaterialConsumedFromPumps = computed(() => {
  const total = {
    cement1: 0,
    cement2: 0,
    slurry: 0,
    additive1: 0,
    additive2: 0,
    additive3: 0,
    colloidalSilica: 0,
    accelerator: 0,
    colloidalAccelerator: 0
  };
  for (const item of props.totalConsumedMaterialOverviewFromPumps)
  {
    total.cement1 += item.cement1 || 0;
    total.cement2 += item.cement2 || 0;
    total.slurry += item.slurry || 0;
    total.additive1 += item.additive1 || 0;
    total.additive2 += item.additive2 || 0;
    total.additive3 += item.additive3 || 0;
    total.colloidalSilica += item.colloidalSilica || 0;
    total.accelerator += item.accelerator || 0;
    total.colloidalAccelerator += item.colloidalAccelerator || 0;
  }
  return total;
});

const headers = [
  { title: 'Hole No', key: 'holeNo', align: 'start' as const },
  { title: 'Cement 1 (kg)', key: 'cement1', align: 'end' as const },
  { title: 'Cement 2 (kg)', key: 'cement2', align: 'end' as const },
  { title: 'Accel (kg)', key: 'accelerator', align: 'end' as const },
  { title: 'Coll. Silica (kg)', key: 'colloidalSilica', align: 'end' as const },
  { title: 'Add. 1 (L)', key: 'additive1', align: 'end' as const },
  { title: 'Add. 2 (L)', key: 'additive2', align: 'end' as const },
  { title: 'Slurry (L)', key: 'slurry', align: 'end' as const },
  { title: 'Total Vol (L)', key: 'volume', align: 'end' as const },
];

// --- Bar chart ---
const chartContainer = ref<HTMLDivElement>()
let chart: any = undefined

const barData = computed(() => {
  if (!props.totalConsumedMaterialOverviewFromPumps?.length) return []
  return props.totalConsumedMaterialOverviewFromPumps.map(item => ({
    category: `H${item.holeNo}`,
    value: (item.cement1 || 0) + (item.cement2 || 0)
  }))
})

const initChart = () => {
  if (!chartContainer.value) return
  const lc = useUseLightningCharts()
  // AMV brand palette
  const AMV_BLUE   = ColorHEX('#041e43')  // bars
  const TEXT_DARK  = ColorHEX('#1e2d3d')  // title + labels
  const TEXT_MID   = ColorHEX('#4b5e6d')  // axis tick labels
  const PAGE_BG    = ColorHEX('#f5f5f5')  // matches page background

  const axisLabelFont = new FontSettings().setSize(11).setFamily('Arial').setWeight('normal')
  const titleFont     = new FontSettings().setSize(15).setFamily('Arial').setWeight('bold')

  chart = lc.BarChart({ container: chartContainer.value, theme: Themes.light })
    // Title
    .setTitle('Total Cement per Hole (kg)')
    .setTitleFont(titleFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_DARK }))
    .setTitleEffect(false)
    // Backgrounds
    .setBackgroundFillStyle(new SolidFill({ color: PAGE_BG }))
    .setBackgroundStrokeStyle(emptyLine)
    .setSeriesBackgroundFillStyle(new SolidFill({ color: ColorHEX('#ffffff') }))
    .setSeriesBackgroundEffect(false)
    // Bars — tighter margin = wider bars when many holes are shown
    .setCornerRadius(3)
    .setBarsMargin(0.05)
    .setBarsEffect(false)
    // Animations & sorting
    .setAnimationsEnabled(false)
    .setSorting(BarChartSorting.Disabled)
    // No value labels above bars — too cluttered with 50 holes; cursor tooltip handles it
    .setValueLabels(undefined)
    // Category labels (H1, H2, …)
    .setCategoryLabels({
      labelFont: axisLabelFont,
      labelFillStyle: new SolidFill({ color: TEXT_MID })
    })

  // Value axis — tick labels + unit title
  chart.valueAxis
    .setTickStrategy(AxisTickStrategies.Numeric, (ticks: any) =>
      ticks.setMajorTickStyle((major: any) =>
        major
          .setLabelFont(axisLabelFont)
          .setLabelFillStyle(new SolidFill({ color: TEXT_MID }))
      )
    )
    .setTitle('kg')
    .setTitleFont(axisLabelFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_MID }))

  if (barData.value.length > 0) {
    applyChartData(barData.value)
  }
}

onMounted(() => {
  initChart()
})

onUnmounted(() => {
  chart?.dispose()
  chart = undefined
})

const applyChartData = (data: { category: string; value: number }[]) => {
  chart.setData(data)
  chart.getSeries().forEach((bar: any) => {
    bar.setFillStyle(new SolidFill({ color: ColorHEX('#041e43') }))
       .setStrokeStyle(emptyLine)
  })
}

watch(barData, (newData) => {
  if (!chart || !newData.length) return
  applyChartData(newData)
})
</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else>
    
    <v-row class="mb-6">
      <!-- Total Consumption (Pumps) -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Total Consumption (Pumps)
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text v-if="Object.keys(extractTotalMaterialConsumedFromPumps).length > 0">
            <v-row dense>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 1</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.cement1) }} kg</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 2</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.cement2) }} kg</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Accelerator</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.accelerator) }} kg</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Colloidal Silica</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.colloidalSilica) }} kg</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive 1</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.additive1) }} L</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive 2</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.additive2) }} L</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive 3</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.additive3) }} L</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Slurry</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.slurry) }} L</div></v-col>
            </v-row>
          </v-card-text>
          <v-card-text v-else class="text-center text-medium-emphasis font-italic pa-8">
            No pump data available
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Total Consumption (Events/Scale) -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Total Consumption (Events/Scale)
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text v-if="Object.keys(totalMaterialConsumedFromEvents).length > 0">
            <v-row dense>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 1</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialConsumedFromEvents.cement1) }} kg</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 2</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialConsumedFromEvents.cement2) }} kg</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Accelerator</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialConsumedFromEvents.accelerator) }} kg</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Coll. Accel</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialConsumedFromEvents.colloidalAccelerator) }} kg</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive 1</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialConsumedFromEvents.additive1) }} L</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive 2</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialConsumedFromEvents.additive2) }} L</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive 3</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialConsumedFromEvents.additive3) }} L</div></v-col>
              <v-col cols="6" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Slurry</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialConsumedFromEvents.slurry) }} L</div></v-col>
            </v-row>
          </v-card-text>
          <v-card-text v-else class="text-center text-medium-emphasis font-italic pa-8">
            No event data available
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Bar Chart -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
        Consumption Overview
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <div ref="chartContainer" style="height: 350px; width: 100%;"></div>
      </v-card-text>
    </v-card>

    <!-- Detailed Table -->
    <v-card elevation="2">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <div class="d-flex align-center">
          <span class="text-subtitle-1 font-weight-bold mr-3">Consumption per Hole</span>
          <v-chip size="small" variant="flat" color="blue-grey-lighten-5" class="text-blue-grey-darken-2 font-weight-bold">
            {{ props.totalConsumedMaterialOverviewFromPumps.length }} Holes
          </v-chip>
        </div>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="props.totalConsumedMaterialOverviewFromPumps"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.holeNo="{ item }">
          <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
            {{ item.holeNo }}
          </v-chip>
        </template>

        <template v-slot:item.cement1="{ item }"><span class="font-monospace">{{ fmt(item.cement1) }}</span></template>
        <template v-slot:item.cement2="{ item }"><span class="font-monospace">{{ fmt(item.cement2) }}</span></template>
        <template v-slot:item.accelerator="{ item }"><span class="font-monospace">{{ fmt(item.accelerator) }}</span></template>
        <template v-slot:item.colloidalSilica="{ item }"><span class="font-monospace">{{ fmt(item.colloidalSilica) }}</span></template>
        <template v-slot:item.additive1="{ item }"><span class="font-monospace">{{ fmt(item.additive1) }}</span></template>
        <template v-slot:item.additive2="{ item }"><span class="font-monospace">{{ fmt(item.additive2) }}</span></template>
        <template v-slot:item.slurry="{ item }"><span class="font-monospace">{{ fmt(item.slurry) }}</span></template>

        <template v-slot:item.volume="{ item }">
          <span class="font-weight-bold text-high-emphasis bg-blue-lighten-5 px-2 py-1 rounded font-monospace">
            {{ fmt(item.volume) }}
          </span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>